import { useTranslation } from "react-i18next";
import type { Manual, ManualRatings, Rating } from "@arc/contracts";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/initials";
import { formatRelativeTime } from "@/lib/date";
import { ManualStatusStepper } from "../ManualStatusStepper.js";

/**
 * The viewer's start / description page (ADR-002 Phase 5): the status stepper + the description + the READ-ONLY average
 * rating + comments (NO rating input — rating happens on the last step, Phase 7) + the Starten button. Mirrors the legacy
 * read-mode landing (`ARLMS_RefactoredReadMode.js`) minus the rating widget, per the owner's mockup.
 */
export function ManualStartPage({
  manual,
  ratings,
  ratingsLoading,
  hasSteps,
  onStart,
}: {
  manual: Manual;
  ratings: ManualRatings | null;
  ratingsLoading: boolean;
  hasSteps: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col gap-6 overflow-auto p-6">
      <ManualStatusStepper state={manual.state} comment={manual.markedComment} />
      <section aria-label={t("viewer.description")}>
        {manual.description ? (
          // Server-sanitized at the read boundary (ADR-013) — safe to render as formatted HTML.
          <div
            className="text-sm leading-relaxed [&_a]:underline [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: manual.description }}
          />
        ) : (
          <p className="text-muted-foreground text-sm">{t("viewer.noDescription")}</p>
        )}
      </section>
      <Separator />
      <RatingSummary ratings={ratings} loading={ratingsLoading} />
      <div className="mt-auto">
        <Button size="lg" onPress={onStart} isDisabled={!hasSteps} className="w-full">
          {hasSteps ? t("other.startButtonText") : t("viewer.noStepsYet")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The read-only ratings summary (ADR-021 read; owner mockup #15): the average + a 5→1 star **histogram**, then the full
 * rating history as an avatar feed with dividers. NO rating input and NO filters here (rating happens on finish, Phase 7);
 * the distribution + "with comment" count are derived client-side from the complete list the API already returns.
 */
function RatingSummary({ ratings, loading }: { ratings: ManualRatings | null; loading: boolean }) {
  const { t } = useTranslation();
  if (loading) return <p className="text-muted-foreground text-sm">{t("viewer.loadingRatings")}</p>;
  if (!ratings || ratings.count === 0) return <p className="text-muted-foreground text-sm">{t("viewer.noRatingsYet")}</p>;

  const list = ratings.ratings;
  const withComment = list.filter((r) => r.comment && r.comment.trim() !== "").length;
  // Per-star tallies from the full list (index 0 = 5★ … index 4 = 1★), so the bars sum to the visible history.
  const buckets = [5, 4, 3, 2, 1].map((star) => ({ star, count: list.filter((r) => r.rating === star).length }));
  const maxBucket = Math.max(1, ...buckets.map((b) => b.count));
  const avg = ratings.average ?? 0;

  return (
    <section aria-label={t("viewer.ratings")} className="flex flex-col gap-4">
      <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t("viewer.ratings")}</h2>

      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-8">
        <div className="shrink-0">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl leading-none font-bold tabular-nums">{avg.toFixed(1)}</span>
            <span className="text-muted-foreground text-sm">/ 5</span>
          </div>
          <StarRow value={avg} className="mt-2" />
          <p className="text-muted-foreground mt-2 text-sm">
            {t("viewer.countRatings", { count: ratings.count })}
            {withComment > 0 ? t("viewer.withComment", { count: withComment }) : ""}
          </p>
        </div>

        <ul className="flex flex-1 list-none flex-col gap-1.5 p-0">
          {buckets.map((b) => (
            <li key={b.star} className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground w-3 text-right tabular-nums">{b.star}</span>
              <Star aria-hidden="true" className="text-muted-foreground size-3 shrink-0" />
              <span className="bg-muted h-2 flex-1 overflow-hidden rounded-full" aria-hidden="true">
                <span className="bg-primary block h-full rounded-full" style={{ width: `${(b.count / maxBucket) * 100}%` }} />
              </span>
              <span className="text-muted-foreground w-4 text-right tabular-nums">{b.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <ul className="divide-border flex list-none flex-col divide-y p-0">
        {list.map((r) => (
          <RatingRow key={r.id} rating={r} />
        ))}
      </ul>
    </section>
  );
}

/** A 5-star row filled up to `value` (rounded) — amber, the app's rating convention. Decorative (the numeric average
 *  carries the value for assistive tech), so it's `aria-hidden`. */
function StarRow({ value, className }: { value: number; className?: string }) {
  const filled = Math.round(value);
  return (
    <div aria-hidden="true" className={cn("flex gap-0.5", className)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={cn("size-4", n <= filled ? "fill-amber-500 text-amber-500" : "text-muted-foreground")} />
      ))}
    </div>
  );
}

/** One entry in the rating history: avatar + name + stars + relative time, then the comment (or an italic placeholder). */
function RatingRow({ rating }: { rating: Rating }) {
  const { t } = useTranslation();
  const name = rating.userName ?? t("viewer.unknownUser");
  return (
    <li className="flex gap-3 py-3 first:pt-0 last:pb-0">
      <Avatar size="sm" className="mt-0.5">
        <AvatarFallback>{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
          <span className="font-medium">{name}</span>
          <StarRow value={rating.rating} />
          <span className="text-muted-foreground text-xs">{formatRelativeTime(rating.createdAt)}</span>
        </div>
        {rating.comment && rating.comment.trim() !== "" ? (
          // Server-sanitized plain text — render as a TEXT node (ADR-021), never HTML.
          <p className="mt-1 text-sm whitespace-pre-line">{rating.comment}</p>
        ) : (
          <p className="text-muted-foreground mt-1 text-sm italic">{t("viewer.ratingNoComment")}</p>
        )}
      </div>
    </li>
  );
}
